import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, MapPin, Mail, Edit, Trash2, Eye } from 'lucide-react';
import { pagePadding } from '../styles/layout';
import { GOLD, BG, CARD_BG, TEXT_MUTED, GOLD_RGBA, INPUT_BG } from '../constants/booking';
import { lookupBooking } from '../services/bookingLookupService';
import { sendSupportRequest } from '../services/emailService';
import { formatStoredBookingDate } from '../utils/bookingDates';

const SESSION_KEY = 'dd_find_booking';

const inputStyle = {
  width: '100%',
  borderRadius: '0.5rem',
  border: `1px solid ${GOLD_RGBA}, 0.35)`,
  backgroundColor: INPUT_BG,
  padding: '0.75rem 1rem',
  color: '#F5F0E8',
  fontSize: '1rem',
  outline: 'none',
  boxSizing: 'border-box',
};

function getStatusColor(status) {
  const map = {
    confirmed: '#10b981',
    booked: '#f59e0b',
    hold: '#3b82f6',
    pending: '#f59e0b',
    cancelled: '#ef4444',
    released: '#6b7280',
    expired: '#6b7280',
    completed: '#6b7280',
  };
  return map[(status || '').toLowerCase()] || '#f59e0b';
}

function getStatusText(status) {
  if (!status) return 'Pending';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function getDurationHours(booking) {
  if (booking.totalHours != null) return booking.totalHours;
  return (booking.baseHours || 0) + (booking.extraHours || 0);
}

export default function FindBooking() {
  const [isMobile, setIsMobile] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [email, setEmail] = useState('');
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [activeAction, setActiveAction] = useState(null);
  const [actionText, setActionText] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [sentRequests, setSentRequests] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('findBookingSentRequests') || '{}');
    } catch {
      return {};
    }
  });

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('findBookingSentRequests', JSON.stringify(sentRequests));
    } catch {
      /* ignore */
    }
  }, [sentRequests]);

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.booking) {
          setBooking(parsed.booking);
          setConfirmationCode(parsed.confirmationCode || '');
          setEmail(parsed.email || '');
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  const handleLookup = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    setActiveAction(null);
    setActionText('');

    try {
      const result = await lookupBooking({ confirmationCode, email });
      setBooking(result);
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({
          booking: result,
          confirmationCode: confirmationCode.trim().toUpperCase(),
          email: email.trim(),
        })
      );
    } catch (err) {
      setBooking(null);
      sessionStorage.removeItem(SESSION_KEY);
      setError(err.message || 'Invalid code or email.');
    } finally {
      setLoading(false);
    }
  };

  const clearLookup = () => {
    setBooking(null);
    setActiveAction(null);
    setActionText('');
    setError('');
    setMessage('');
    sessionStorage.removeItem(SESSION_KEY);
  };

  const sendRequest = async (type) => {
    if (!booking || !actionText.trim()) return;
    setEmailLoading(true);
    setError('');

    try {
      const duration = getDurationHours(booking);
      await sendSupportRequest({
        subject: `Booking ${type} Request - ${booking.packageName || 'Golf Simulator'}`,
        htmlContent: `
          <div style="font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; line-height: 1.6; color: #111827;">
            <h2 style="margin: 0 0 12px;">Booking ${type} Request</h2>
            <p>A guest requested a ${type.toLowerCase()} via Find Booking.</p>
            <h3>Customer</h3>
            <ul>
              <li><strong>Name:</strong> ${booking.customerName || 'Guest'}</li>
              <li><strong>Email:</strong> ${booking.email || email || 'N/A'}</li>
              <li><strong>Phone:</strong> ${booking.phone || 'N/A'}</li>
            </ul>
            <h3>Booking</h3>
            <ul>
              <li><strong>Package:</strong> ${booking.packageName || 'N/A'}</li>
              <li><strong>Date:</strong> ${booking.date || 'N/A'}</li>
              <li><strong>Time:</strong> ${booking.time || 'N/A'}</li>
              <li><strong>Duration:</strong> ${duration || 'N/A'} hours</li>
              <li><strong>Status:</strong> ${booking.status || 'Unknown'}</li>
              <li><strong>Confirmation:</strong> ${booking.confirmationCode || booking.bookingId || 'N/A'}</li>
            </ul>
            <h3>Message</h3>
            <p style="white-space: pre-wrap;">${actionText}</p>
          </div>
        `,
        textContent:
          `Booking ${type} Request\n\n` +
          `Customer: ${booking.customerName || 'Guest'}\n` +
          `Email: ${booking.email || email || 'N/A'}\n` +
          `Package: ${booking.packageName || 'N/A'}\n` +
          `Date: ${booking.date || 'N/A'}\n` +
          `Time: ${booking.time || 'N/A'}\n` +
          `Confirmation: ${booking.confirmationCode || booking.bookingId || 'N/A'}\n\n` +
          `Message:\n${actionText}`,
      });

      setSentRequests((prev) => ({
        ...prev,
        [booking.id]: {
          ...prev[booking.id],
          [type.toLowerCase()]: true,
        },
      }));
      setMessage(`${type} request sent. We'll respond within 24 hours.`);
      setActiveAction(null);
      setActionText('');
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      setError(err.message || `Failed to send ${type.toLowerCase()} request.`);
    } finally {
      setEmailLoading(false);
    }
  };

  const durationHours = booking ? getDurationHours(booking) : 0;
  const alreadySent = (type) => Boolean(sentRequests[booking?.id]?.[type]);

  return (
    <div
      className="dd-page"
      style={{
        minHeight: '100vh',
        backgroundColor: BG,
        color: '#F5F0E8',
        padding: pagePadding(isMobile),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
      }}
    >
      <Helmet>
        <title>Find Booking | Digital Divot Golf</title>
        <meta
          name="description"
          content="Look up your Digital Divot booking with the confirmation code from your email."
        />
      </Helmet>

      <div style={{ maxWidth: '40rem', width: '100%', margin: '0 auto' }}>
        <h1
          style={{
            fontSize: isMobile ? '1.75rem' : '2.25rem',
            fontWeight: '800',
            color: GOLD,
            margin: '0 0 0.75rem',
            textAlign: 'center',
          }}
        >
          Find Your Booking
        </h1>
        <p
          style={{
            textAlign: 'center',
            color: TEXT_MUTED,
            margin: '0 0 2rem',
            lineHeight: 1.6,
          }}
        >
          Enter the confirmation or hold code from your email and the email address you used.
        </p>

        {!booking ? (
          <form
            onSubmit={handleLookup}
            style={{
              backgroundColor: CARD_BG,
              border: `1px solid ${GOLD_RGBA}, 0.25)`,
              borderRadius: '1rem',
              padding: isMobile ? '1.25rem' : '1.75rem',
            }}
          >
            <label style={{ display: 'block', marginBottom: '1rem' }}>
              <span style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, color: GOLD }}>
                Confirmation code
              </span>
              <input
                type="text"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value.toUpperCase())}
                autoComplete="off"
                required
                placeholder="e.g. ABC123"
                style={{ ...inputStyle, letterSpacing: '0.08em', fontFamily: 'monospace' }}
              />
            </label>

            <label style={{ display: 'block', marginBottom: '1.25rem' }}>
              <span style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, color: GOLD }}>
                Booking email
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                placeholder="you@example.com"
                style={inputStyle}
              />
            </label>

            {error && (
              <p style={{ color: '#f87171', margin: '0 0 1rem', fontSize: '0.9375rem' }}>{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                border: 'none',
                borderRadius: '9999px',
                backgroundColor: GOLD,
                color: '#000',
                fontWeight: 700,
                padding: '0.85rem 1.25rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Looking up…' : 'Find Booking'}
            </button>
          </form>
        ) : (
          <div
            style={{
              backgroundColor: CARD_BG,
              border: `1px solid ${GOLD_RGBA}, 0.25)`,
              borderRadius: '1rem',
              padding: isMobile ? '1.25rem' : '1.75rem',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '1rem',
                marginBottom: '1.25rem',
              }}
            >
              <div>
                <h2 style={{ margin: '0 0 0.35rem', fontSize: '1.35rem', color: '#F5F0E8' }}>
                  {booking.packageName || 'Golf Simulator'}
                </h2>
                <p style={{ margin: 0, color: TEXT_MUTED, fontSize: '0.875rem' }}>
                  Code{' '}
                  <span style={{ fontFamily: 'monospace', color: GOLD, fontWeight: 700 }}>
                    {booking.confirmationCode || booking.bookingId}
                  </span>
                </p>
              </div>
              <span
                style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '999px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  backgroundColor: `${getStatusColor(booking.status)}22`,
                  color: getStatusColor(booking.status),
                  whiteSpace: 'nowrap',
                }}
              >
                {getStatusText(booking.status)}
              </span>
            </div>

            {(booking.kind === 'hold' || String(booking.status || '').toUpperCase() === 'HOLD') && (
              <p style={{ color: '#93c5fd', backgroundColor: 'rgba(37, 99, 235, 0.18)', borderRadius: '0.75rem', padding: '0.75rem 1rem', margin: '0 0 1.25rem', fontSize: '0.9375rem', lineHeight: 1.5 }}>
                This is a calendar hold, not a paid booking. We reserved this window while we confirm details with you.
              </p>
            )}

            <div style={{ display: 'grid', gap: '0.65rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar size={16} color={GOLD} />
                <span>{formatStoredBookingDate(booking.date)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={16} color={GOLD} />
                <span>
                  {booking.time || 'TBD'}
                  {durationHours ? ` · ${durationHours} hour${durationHours !== 1 ? 's' : ''}` : ''}
                </span>
              </div>
              {booking.venueAddress && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <MapPin size={16} color={GOLD} style={{ marginTop: 2 }} />
                  <span>{booking.venueAddress}</span>
                </div>
              )}
              {booking.email && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Mail size={16} color={GOLD} />
                  <span>{booking.email}</span>
                </div>
              )}
            </div>

            {booking.kind !== 'hold' && String(booking.status || '').toUpperCase() !== 'HOLD' && (
            <div
              style={{
                borderTop: `1px solid ${GOLD_RGBA}, 0.2)`,
                paddingTop: '1rem',
                marginBottom: '1.25rem',
                display: 'grid',
                gap: '0.35rem',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: TEXT_MUTED }}>Total</span>
                <strong>${booking.total ?? 0}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: TEXT_MUTED }}>Deposit paid</span>
                <strong style={{ color: GOLD }}>${booking.deposit ?? 0}</strong>
              </div>
            </div>
            )}

            {message && (
              <p style={{ color: '#34d399', margin: '0 0 1rem', fontSize: '0.9375rem' }}>{message}</p>
            )}
            {error && (
              <p style={{ color: '#f87171', margin: '0 0 1rem', fontSize: '0.9375rem' }}>{error}</p>
            )}

            <p style={{ color: TEXT_MUTED, fontSize: '0.875rem', marginBottom: '1rem' }}>
              Need changes? Send a request below or email admin@digitaldivotgolf.com.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
              {[
                { key: 'reschedule', label: 'Reschedule', icon: Edit },
                { key: 'cancel', label: 'Cancel', icon: Trash2 },
                { key: 'details', label: 'Ask a Question', icon: Eye },
              ].map(({ key, label, icon: Icon }) => {
                const sent = alreadySent(key);
                const typeLabel = key === 'details' ? 'Details' : key.charAt(0).toUpperCase() + key.slice(1);
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={sent}
                    onClick={() => {
                      setActiveAction(activeAction === key ? null : key);
                      setActionText('');
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      border: `1px solid ${sent ? '#10b981' : GOLD}`,
                      backgroundColor: sent ? 'rgba(16,185,129,0.12)' : 'transparent',
                      color: sent ? '#34d399' : GOLD,
                      borderRadius: '999px',
                      padding: '0.55rem 0.9rem',
                      fontWeight: 600,
                      fontSize: '0.8125rem',
                      cursor: sent ? 'default' : 'pointer',
                    }}
                  >
                    <Icon size={14} />
                    {sent ? 'Request Sent' : label}
                    {!sent && activeAction === key ? ` (${typeLabel})` : ''}
                  </button>
                );
              })}
            </div>

            {activeAction && !alreadySent(activeAction) && (
              <div style={{ marginBottom: '1.25rem' }}>
                <textarea
                  value={actionText}
                  onChange={(e) => setActionText(e.target.value)}
                  rows={4}
                  placeholder={
                    activeAction === 'reschedule'
                      ? 'Tell us your preferred new date and time…'
                      : activeAction === 'cancel'
                        ? 'Tell us why you need to cancel…'
                        : 'What do you need help with?'
                  }
                  style={{ ...inputStyle, resize: 'vertical', marginBottom: '0.75rem' }}
                />
                <button
                  type="button"
                  disabled={!actionText.trim() || emailLoading}
                  onClick={() =>
                    sendRequest(
                      activeAction === 'details'
                        ? 'Details'
                        : activeAction.charAt(0).toUpperCase() + activeAction.slice(1)
                    )
                  }
                  style={{
                    border: 'none',
                    borderRadius: '9999px',
                    backgroundColor: GOLD,
                    color: '#000',
                    fontWeight: 700,
                    padding: '0.7rem 1.25rem',
                    cursor: !actionText.trim() || emailLoading ? 'not-allowed' : 'pointer',
                    opacity: !actionText.trim() || emailLoading ? 0.6 : 1,
                  }}
                >
                  {emailLoading ? 'Sending…' : 'Send Request'}
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={clearLookup}
              style={{
                background: 'none',
                border: 'none',
                color: TEXT_MUTED,
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: 0,
                fontSize: '0.875rem',
              }}
            >
              Look up a different booking
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
